// Shared Styled components.
import _ from 'lodash';
import React from 'react';
import styled from 'styled-components';

import {IntersperseItem, intersperseNodes} from '../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {CleanHtml} from '../../../../libs/shared/core/src/helpers/typescript/cleanHtml';
import {PaletteColorsEnum} from '../../../../libs/shared/core/src/models/api/paletteModel';
import {TimelineVariationsEnum} from '../../../../libs/shared/core/src/store/timelineVariations/timelineVariationsTypes';
import {LinkTrap} from '../../../../libs/shared/ui/src/components/links/linkTrap';
import {LinkTooltips} from '../../../../libs/shared/ui/src/components/tooltips/linkTooltips';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis, textSelection} from '../../../../libs/shared/ui/src/styles/mixins';
import {ArbitraryCalendarEventModel} from '../../../models/api/arbitraryCalendarEventModel';
import {CalendarEventVersionModel} from '../../../models/api/calendarEventVersionModel';
import {CalendarEventLocationRenderer} from '../../common/calendarEvent/calendarEventLocationRenderer';
import {MessageHtml} from '../../inboxes/viewer/activity/message/messageHtml';
import {ConferenceToken} from '../editor/conferenceEditor/conferenceToken';
import {CalendarEventDetailRow} from './calendarEventDetailRow';

interface EventColorStyleProps {
  $color: PaletteColorsEnum;
}

export const StyledEventColorDiv = styled.div<EventColorStyleProps>`
  background-color: ${(p) => p.theme.palette[p.$color].shade40};
  height: 12px;
  width: 12px;
  border-radius: 3px;
  margin-top: 11px;
`;

export const StyledSummaryWrapperDiv = styled.div`
  margin-bottom: 4px;
`;

interface SummaryStyleProps {
  $isCancelled: boolean;
}
export const StyledSummaryDiv = styled.div<SummaryStyleProps>`
  color: ${(p) => p.theme.greys.shade80};
  font-size: ${fontSizes.veryLarge};
  font-weight: ${fontWeights.semibold};
  line-height: 28px;
  text-decoration: ${(p) => (p.$isCancelled ? 'line-through' : 'none')};
  ${textSelection()}
`;

export const StyledTextDiv = styled.div`
  line-height: 21px;
  white-space: pre-wrap;
  min-width: 0px;
  overflow: hidden;
  ${textSelection()}

  p {
    margin: 0px;
  }

  a {
    color: ${(p) => p.theme.interactiveText};
    cursor: pointer;

    &:hover {
      color: ${(p) => p.theme.interactiveTextHover};
    }
  }
`;

export const StyledCalendarNameDiv = styled.div`
  ${ellipsis()}
`;

// Shared functions.

export function maybeRenderDescriptionRow(
  descriptionHtml: CleanHtml | undefined,
  descriptionIsEmpty: boolean,
) {
  if (!descriptionHtml || descriptionIsEmpty) {
    return null;
  }

  const descriptionContent = (
    <StyledTextDiv>
      <LinkTooltips>
        <LinkTrap>
          <MessageHtml
            trustedHtml={descriptionHtml}
            timelineVariation={TimelineVariationsEnum.ASYNCHRONOUS}
          />
        </LinkTrap>
      </LinkTooltips>
    </StyledTextDiv>
  );

  return <CalendarEventDetailRow iconName="notesSmall" content={descriptionContent} />;
}

export function maybeRenderConferenceRow(
  calendarEventVersion: CalendarEventVersionModel | ArbitraryCalendarEventModel,
) {
  if (!calendarEventVersion.conference) {
    return null;
  }

  return (
    <CalendarEventDetailRow
      iconName="videoSmall"
      content={<ConferenceToken conference={calendarEventVersion.conference} />}
    />
  );
}

export function maybeRenderLocationsRow(
  calendarEventVersion: CalendarEventVersionModel | ArbitraryCalendarEventModel,
) {
  if (calendarEventVersion.locations.length === 0) {
    return null;
  }

  const locationsRender: ReadonlyArray<IntersperseItem> = calendarEventVersion.locations.map((l, index) => ({
    key: index,
    node: <CalendarEventLocationRenderer location={l} />,
  }));

  const locationText = _(calendarEventVersion.locations)
    .map((l) => l.displayName)
    .compact()
    .value()
    .join(', ');

  if (!locationText) {
    return null;
  }

  const locationsContent = <StyledTextDiv>{intersperseNodes(locationsRender, () => ',')}</StyledTextDiv>;
  return <CalendarEventDetailRow iconName="locationSmall" content={locationsContent} />;
}
